import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSchool } from '../../contexts/SchoolContext';
import { buildUserTodos, type TodoItem, TODO_ICONS } from '../../lib/todos';

const PRIORITY_DOT: Record<TodoItem['priority'], string> = {
  high: 'bg-red-500', medium: 'bg-amber-400', low: 'bg-blue-400',
};
/** The deadline carries the urgency too — a date three days past and one three
 *  weeks away read identically in grey, and the priority dot is easy to miss. */
const DUE_TONE: Record<TodoItem['priority'], string> = {
  high: 'font-medium text-red-600', medium: 'text-amber-700', low: 'text-gray-400',
};
const MAX_SHOWN = 5;

/** The home dashboard's to-do list — the top few things needing this user's
 *  attention, with the full list a click away. */
export default function HomeTodos() {
  const { appUser } = useAuth();
  const { activeModules, schoolLoading } = useSchool();
  const [todos, setTodos] = useState<TodoItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Some section did not load: what did load is shown, with a line saying so.
  const [incomplete, setIncomplete] = useState(false);

  const uid = appUser?.id ?? '';
  const schoolId = appUser?.schoolId ?? '';
  const role = appUser?.role;
  const staffCategory = appUser?.staffCategory;
  // Keyed on the contents, not the array: SchoolContext rebuilds activeModules
  // whenever any of its six documents arrives, and each rebuild re-ran the
  // whole list (for an admin, the full staff directory) and threw away the one
  // before — so Home showed only the LAST of several builds, and a hang in that
  // one kept the card on "Loading…" even when an earlier build had finished.
  const modulesKey = activeModules.join(',');

  useEffect(() => {
    if (!uid || !schoolId || !role || schoolLoading) return;
    let live = true;
    buildUserTodos(uid, schoolId, role, staffCategory, activeModules)
      .then((r) => {
        if (!live) return;
        // Nothing loaded AND something failed is a failure, not an empty
        // list: "you're all caught up" is the one thing this card must not
        // say when it could not look.
        if (r.items.length === 0 && r.incomplete) { setFailed(true); return; }
        setTodos(r.items); setIncomplete(r.incomplete); setFailed(false);
      })
      .catch((err) => { console.error('[HomeTodos] could not build the list:', err); if (live) setFailed(true); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, schoolId, role, staffCategory, modulesKey, schoolLoading]);

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">To do</h2>
        {todos && todos.length > 0 && role !== 'student' && (
          <Link to="/todo" className="text-xs font-medium text-brand-600 hover:underline">
            {todos.length > MAX_SHOWN ? `View all (${todos.length}) →` : 'Open To Do →'}
          </Link>
        )}
      </div>

      {failed && todos === null ? (
        <div className="card flex items-center justify-between gap-3 p-4 text-sm">
          <span className="text-amber-700">Couldn’t load your to-do list.</span>
          {/* A reload, not a retry: a connection stuck the way that causes this
              stays stuck for every read until the page starts a new one. */}
          <button onClick={() => window.location.reload()} className="btn-secondary shrink-0 text-xs">Try again</button>
        </div>
      ) : todos === null ? (
        <div className="card p-6 text-center text-sm text-gray-300">Loading…</div>
      ) : todos.length === 0 ? (
        <div className="card flex items-center gap-3 p-4 text-sm text-gray-500">
          <span className="text-xl" aria-hidden>🎉</span>
          You're all caught up — nothing needs your attention right now.
        </div>
      ) : (
        <div className="card divide-y divide-gray-100 p-0">
          {incomplete && (
            <p className="px-3 py-2 text-xs text-amber-700">Some of your tasks couldn’t be loaded, so this list may be missing things. Refresh to try again.</p>
          )}
          {todos.slice(0, MAX_SHOWN).map((item) => (
            <Link key={item.id} to={item.href} className="flex items-start gap-3 p-3 transition-colors hover:bg-gray-50">
              <span className="mt-0.5 text-lg shrink-0" aria-hidden>{TODO_ICONS[item.type] ?? '•'}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-800">{item.title}</span>
                <span className="block text-xs text-gray-400">{item.subtitle}</span>
                {item.dueDate && (
                  <span className={`mt-0.5 block text-xs ${DUE_TONE[item.priority]}`}>{item.dueDate}</span>
                )}
              </span>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[item.priority]}`} aria-hidden />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
